import {Injectable} from '@angular/core';
import {Apollo} from "apollo-angular";
import {get_dd_checklist_groups, get_project_dd_checklist_group} from "./graphql/getDueDiligence";
import {BehaviorSubject} from "rxjs";
import ProjectDdChecklistGroup from "../interfaces/project-dd-checklist-group";
import ProjectStatus from "../interfaces/project-status";
import {PipelineProject} from "../interfaces/pipeline-project";
import {
  DdItemInfoDateCategory,
  DiligenceItem,
  Panel
} from "./due-diligence-subcomponents/due-diligence-item/due-diligence-item.component";
import {DataFormatService} from "../services/data-format.service";
import {PipelineProjectDate} from "../interfaces/pipeline-project-date";
import Participant from "../interfaces/participant";
import EntityAddress from "../interfaces/entity-address";
import ProjectDdChecklistSelection from "../interfaces/project-dd-checklist-selection";
import {
  add_project_dd_checklist_date,
  add_project_dd_checklist_detail_date,
  add_project_dd_checklist_selection,
  delete_project_dd_checklist_date,
  update_project_dd_checklist_date,
  update_project_dd_checklist_detail_date,
  update_project_dd_checklist_group,
  update_project_dd_checklist_selection
} from "./graphql/updateDueDiligence";
import Permission from "../interfaces/permission";
import projectDdChecklistGroup from "../interfaces/project-dd-checklist-group";

@Injectable({
  providedIn: 'root'
})
export class DueDiligenceService {

  constructor(private apollo: Apollo, private dataFormat: DataFormatService,) { }

  private _loading: boolean = false;
  private loading = new BehaviorSubject(this._loading)
  public loading$ = this.loading.asObservable();

  private _deals: ProjectDdChecklistGroup[] = []
  private deals = new BehaviorSubject(this._deals)
  public deals$ = this.deals.asObservable();

  private _permissions: Permission | null = null
  private permissions = new BehaviorSubject(this._permissions)
  public permissions$ = this.permissions.asObservable()

  private _checklistSections:Panel[] = []
  private checklistSections = new BehaviorSubject(this._checklistSections)
  public checklistSections$ = this.checklistSections.asObservable();

  private _checklists:any[] = []
  private checklists = new BehaviorSubject(this._checklists)
  public checklists$ = this.checklists.asObservable();

  private _statuses:any[] = []
  private statuses = new BehaviorSubject(this._statuses)
  public statuses$ = this.statuses.asObservable()

  private _dates:any[] = []
  private dates = new BehaviorSubject(this._dates)
  public dates$ = this.dates.asObservable()

  private _setLoading(value: boolean){
    this._loading = value;
    this.loading.next(value)
  }

  private _setChecklistSections(ddChecklists:any[]){
    this._checklistSections = []
    ddChecklists.forEach((cklist:any) => {
      if(cklist.active){
        let section: Panel = {
          title: cklist.name,
          items: []
        }
        this._checklistSections.push(section);
      }
    })
    this.checklistSections.next(this._checklistSections)
  }

  private _setProjects(deals: ProjectDdChecklistGroup[], user_id?:string | null){
    this._deals = []
    if(user_id!==null){
      deals.forEach((deal:ProjectDdChecklistGroup) => {
        const hasUserParticipant = deal?.project?.participants?.find((par:Participant) => par?.person?.id == user_id)
        if(hasUserParticipant){
          this._deals.push(deal)
        }
        this.sortByCategory("closing")
      })
    } else {
      this._deals = deals
      this.sortByCategory("closing")
    }
  }

  public sortByCategory(category:string){
    if(category.toLowerCase()==="closing"){
      const sortedDeals = [...this._deals].sort((a:ProjectDdChecklistGroup, b: ProjectDdChecklistGroup) => {
        const aPipelineProject = a.project.pipelineProjects?.filter((ppj: PipelineProject) => ppj.active==true)
        const bPipelineProject = b.project.pipelineProjects?.filter((ppj: PipelineProject) => ppj.active==true)
        const aCloseDate = aPipelineProject?.[0]?.pipelineProjectDates.filter((date:PipelineProjectDate) => {
          return date.active && date?.pipelineMilestone?.milestone?.name.toLowerCase()=='closed'
        })
        const bCloseDate = bPipelineProject?.[0]?.pipelineProjectDates.filter((date:PipelineProjectDate) => {
          return date.active && date?.pipelineMilestone?.milestone?.name.toLowerCase()=='closed'
        })
        if(!aCloseDate || aCloseDate.length==0){
          return 1
        }
        if(!bCloseDate || bCloseDate.length==0){
          return -1
        }
        return (aCloseDate[0].date ?? '').localeCompare(bCloseDate[0].date ?? '')
      })
      this._deals = sortedDeals
      this.deals.next(this._deals)
    }
    if(category.toLowerCase()=='screening'){
      const sortedDeals = [...this._deals].sort((a:ProjectDdChecklistGroup, b: ProjectDdChecklistGroup) => {
        const aPipelineProject = a.project.pipelineProjects?.filter((ppj: PipelineProject) => ppj.active==true)
        const bPipelineProject = b.project.pipelineProjects?.filter((ppj: PipelineProject) => ppj.active==true)
        const aCloseDate = aPipelineProject?.[0]?.pipelineProjectDates.filter((date:PipelineProjectDate) => {
          return date.active && date?.pipelineMilestone?.milestone?.name.toLowerCase()=='screening committee'
        })
        const bCloseDate = bPipelineProject?.[0]?.pipelineProjectDates.filter((date:PipelineProjectDate) => {
          return date.active && date?.pipelineMilestone?.milestone?.name.toLowerCase()=='screening committee'
        })
        if(!aCloseDate || aCloseDate.length==0){
          return 1
        }
        if(!bCloseDate || bCloseDate.length==0){
          return -1
        }
        return (aCloseDate[0].date ?? '').localeCompare(bCloseDate[0].date ?? '')
      })
      this._deals = sortedDeals
      this.deals.next(this._deals)
    }
    if(category.toLowerCase()==='project'){
      const sortedDeals = [...this._deals].sort((a:ProjectDdChecklistGroup, b: ProjectDdChecklistGroup) => {
        return a.project.name.localeCompare(b.project.name)
      })
      this._deals = sortedDeals
      this.deals.next(this._deals)
    }
    this.deals.next(this._deals)
  }

  private _setChecklists(ddChecklists:any[]){
    this._checklists = []
    ddChecklists.forEach((cklist:any) => {
      if(cklist.active){
        let obj = {...cklist}
        obj.ddChecklistItems = cklist.ddChecklistItems.filter((item:any) => item.active)
        this._checklists.push(obj)
      }
    })
    this.checklists.next(this._checklists)
  }

  public getChecklists(name: string, userMode?: boolean){
    this.apollo.query({
      query: get_dd_checklist_groups,
      variables: {
        name: name
      },
      fetchPolicy: 'network-only'
    }).subscribe((response:any) => {
      let [group = null] = response.data?.ddChecklistGroups ?? []
      this._permissions = response.data?.me.permissionsForResource
      this.permissions.next(this._permissions)
      this._dates = response.data?.ddChecklistDates
      this.dates.next(this._dates)
      this._statuses = response.data?.ddChecklistStates
      this.statuses.next(this._statuses)
      this._setChecklists(group.ddChecklists)
      let personId = response.data?.me?.user_profile?.user?.person?.id ?? null
      this._setProjects(group.projectDdChecklistGroups, userMode ? personId : null)
      this._setChecklistSections(group.ddChecklists)
      this._setLoading(false)
    })
  }


  public _formatProjectChecklistGroups(item: any){
    let ltEquity = item.project.ltmodels.length>0
    && item.project.ltmodels[0].kpiset!==null && item.project.ltmodels[0].kpiset.kpiset_detail!==null
      ? item.project.projectDetail.lt_equity_amount : null

    let noi = item.project.ltmodels.length>0
    && item.project.ltmodels[0].kpiset!==null && item.project.ltmodels[0].kpiset.kpiset_detail!==null
      ? item.project.ltmodels[0].kpiset.kpiset_detail.net_operating_income : null

    let fund = item.project.projectCredits.length>0 && item.project.projectCredits[0].projectCreditOutlets.length > 0 ?
      item.project.projectCredits[0].projectCreditOutlets[0].fund : null

    let status = item.project.projectStatuses.find((pStatus: ProjectStatus) => pStatus?.statusSequence?.name.toLowerCase() == "syndications")
    let pipelineProjects: PipelineProject[] = item.project.pipelineProjects?.filter((pProj: PipelineProject) => pProj?.active == true) ?? []
    let obj:DiligenceItem = {
      name: item.project.name,
      dealUrl: item.project ? this.dataFormat.formatLink(item.project) : null,
      status: !status ? null : status.status.name,
      lt_equity: ltEquity,
      noi: noi,
      fund: fund?.name ?? null,
      fundUrl: fund ? this.dataFormat.formatLink(fund) : null,
      sponsor: this._setParticipants(item.project.participants, 'sponsor')?.name ?? null,
      sponsorUrl: this._setParticipants(item.project.participants, 'sponsor') ? this.dataFormat.formatLink(this._setParticipants(item.project.participants, 'sponsor')) : null,
      acc_manager: this._setParticipants(item.project.participants, 'account manager')?.name ?? null ,
      acc_managerUrl: this._setParticipants(item.project.participants, 'account manager') ? this.dataFormat.formatLink(this._setParticipants(item.project.participants, 'account manager')) : null,
      underwriter: this._setParticipants(item.project.participants, 'underwriter')?.name ?? null,
      underwriterUrl: this._setParticipants(item.project.participants, 'underwriter') ? this.dataFormat.formatLink(this._setParticipants(item.project.participants, 'underwriter')) : null,
      closing_coordinator: this._setParticipants(item.project.participants, 'closing coordinator')?.name ?? null,
      closing_coordinatorUrl: this._setParticipants(item.project.participants, 'closing coordinator') ? this.dataFormat.formatLink(item.project.participants, 'closing coordinator') : null,
      uw_analyst: this._setParticipants(item.project.participants, 'underwriting analyst')?.name ?? null,
      uw_analystUrl: this._setParticipants(item.project.participants, 'underwriting analyst') ? this.dataFormat.formatLink(this._setParticipants(item.project.participants, 'underwriting analyst')) : null,
      construction_analyst: this._setParticipants(item.project.participants, 'construction analyst')?.name ?? null,
      construction_analystUrl: this._setParticipants(item.project.participants, 'construction analyst') ? this.dataFormat.formatLink(this._setParticipants(item.project.participants, 'construction analyst')) : null,
      loi_signed_date: this._setItemDates(pipelineProjects[0].pipelineProjectDates, DdItemInfoDateCategory.LOI_SIGNED) ?? null,
      screening_date: this._setItemDates(pipelineProjects[0].pipelineProjectDates, DdItemInfoDateCategory.SCREENING) ?? null,
      capital_date: this._setItemDates(pipelineProjects[0].pipelineProjectDates, DdItemInfoDateCategory.CAPITAL) ?? null,
      closed_date: this._setItemDates(pipelineProjects[0].pipelineProjectDates, DdItemInfoDateCategory.CLOSED) ?? null,
      address: this._setAddress(item.project.properties[0].entity.entityAddresses) ?? null,
      sharepointUrl: "https://creallc.sharepoint.com/sites/" + item.project.sharepoint_folder,
      timing_issues: item.timing_issues,
      screening_delay: item.screening_delay,
      high_priority: item.high_priority
    }
    return obj
  }

  private _setItemDates(projectDates: PipelineProjectDate[], selectionCategory: DdItemInfoDateCategory){
    if(projectDates.length==0){
      return null
    }
    let milestoneName = selectionCategory==DdItemInfoDateCategory.LOI_SIGNED ? 'LOI Signed' :
      selectionCategory==DdItemInfoDateCategory.CAPITAL ? 'Capital Committee' : selectionCategory==DdItemInfoDateCategory.SCREENING ? 'Screening Committee'
        : selectionCategory==DdItemInfoDateCategory.CLOSED ? 'Closed' : "";
    let milestoneProjectDate = projectDates.filter((pD: PipelineProjectDate) => pD.pipelineMilestone?.milestone?.name===milestoneName && !pD.initial_target)
    if(milestoneProjectDate.length==0){
      return null
    }

    return milestoneProjectDate[0].date
  }

  private _setParticipants(participants: Participant[],roleName: string ){
    if(roleName==='sponsor'){
      let sponsor = participants?.find((par: Participant) => par.sponsor!==null)
      return sponsor?.sponsor ?? null
    }
    let participant = participants?.find((par: Participant) => par.role.name.toLowerCase() == roleName.toLowerCase())
    return participant ? participant.person : null;
  }

  private _setAddress(entityAddresses: EntityAddress[]){
    let addressString = entityAddresses[0].address.address1
    if(entityAddresses[0].address.address2!==null){
      addressString += ", " + entityAddresses[0].address.address2
    }
    addressString += " " + entityAddresses[0].address.zipCode.city + ", "
    + entityAddresses[0].address.zipCode.state.abbreviation + " " + entityAddresses[0].address.zipCode?.zipcode
    return addressString ?? null
  }

  public setSelections(selections: ProjectDdChecklistSelection[]){
    return selections
  }

  public filterGroups(value: string){
    let filteredDeals = []
    if(value.length==0){
      filteredDeals = this._deals
    }
    filteredDeals = this._deals.filter((deal:ProjectDdChecklistGroup) => deal.project.name.toLowerCase().includes(value.toLowerCase()))
    this.deals.next(filteredDeals)
  }

  public getProjectDdChecklistGroup(id: string, next: Function){
    this.apollo.query({
      query: get_project_dd_checklist_group,
      variables: {id},
      fetchPolicy: 'network-only'
    }).subscribe((response:any) => {
      let group = response.data?.projectDdChecklistGroup
      this._deals = this._deals.map((deal:any) => deal.id===group.id ? group : deal)
      this.deals.next(this._deals)
      next()
    })
  }

  public addProjectDdChecklistSelection(groupId: string, itemId: string, statusId: string, date: string){
    return this.apollo.mutate({
      mutation: add_project_dd_checklist_selection,
      variables: {
        project_dd_checklist_group_id: groupId,
        dd_checklist_item_id: itemId,
        dd_checklist_state_id: statusId,
      }
    })
  }

  public updateProjectDdChecklistSelection(selection_id: string | null, status_id: string){
    return this.apollo.mutate({
      mutation: update_project_dd_checklist_selection,
      variables: {
        id: selection_id,
        dd_checklist_state_id: status_id
      }
    })
  }

  public updateProjectDdGroup(id: string, timing_issues: string, high_priority: boolean, screening_delay: number){
    return this.apollo.mutate({
      mutation: update_project_dd_checklist_group,
      variables: {id: id, high_priority: high_priority, timing_issues: timing_issues, screening_delay:screening_delay}
    })
  }

  public addProjectDueDiligenceDate(dd_checklist_state_id: string, item_id: string, date: string){
    return this.apollo.mutate({
      mutation: add_project_dd_checklist_date,
      variables: {dd_checklist_state_id: dd_checklist_state_id, project_dd_checklist_selection_id: item_id, date: date}
    })
  }

  public updateProjectDueDiligenceDate(id: string, date:string){
    return this.apollo.mutate({
      mutation: update_project_dd_checklist_date,
      variables: {
        id: id,
        date: date
      }
    })
  }

  public deleteProjectDueDiligenceDate(id: string){
    return this.apollo.mutate({
      mutation: delete_project_dd_checklist_date,
      variables: {id}
    })
  }

  public addProjectDueDiligenceDetailDate(selection_id: string, checklist_id: string, date: string){
    return this.apollo.mutate({
      mutation: add_project_dd_checklist_detail_date,
      variables: {
        project_dd_checklist_selection_id: selection_id,
        dd_checklist_date_id: checklist_id,
        date: date
      }
    })
  }

  public updateProjectDueDiligenceDetailDate(id: string, date: string){
    return this.apollo.mutate({
      mutation: update_project_dd_checklist_detail_date,
      variables: {
        id: id,
        date: date
      }
    })
  }

}
